import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth handling for auth endpoints - they handle their own errors
  // Note: /auth/me handles token refresh internally via AuthService.initialize()
  if (req.url.includes('/auth/')) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (!isRefreshing) {
          // First 401 request - initiate refresh
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refresh().pipe(
            switchMap((success) => {
              isRefreshing = false;
              refreshTokenSubject.next(success);

              if (success) {
                // Retry the original request
                return next(req);
              }

              // Refresh failed, clear user and redirect to login
              authService.clearUser();
              router.navigate(['/auth/login']);

              return throwError(() => error);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              refreshTokenSubject.next(false);
              authService.clearUser();
              router.navigate(['/auth/login']);

              return throwError(() => refreshError);
            }),
          );
        }

        // Refresh already in progress - wait for it to complete
        return refreshTokenSubject.pipe(
          filter((result): result is boolean => result !== null),
          take(1),
          switchMap((success) => {
            if (success) {
              // Refresh succeeded, retry the original request
              return next(req);
            }

            // Refresh failed
            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
};

